/**
 * RBAC Kill-Switch Flags
 *
 * Runtime overrides for the F-500 unified RBAC pipeline. If a customer
 * hits a regression in production, ops can flip the corresponding env
 * var on Vercel and the next cold-start picks it up — ~30s vs. the
 * 5-10min a git-revert + CI build would take.
 *
 * Two switches, each independent:
 *
 *   RBAC_CACHE_DISABLED=1
 *     Bypasses the in-memory resolver cache entirely. Every call to
 *     /api/admin/active-tenant hits the DB. Use when you suspect the
 *     cache is serving stale or wrong data (e.g. permission revoke
 *     not propagating). Cost: ~5 DB roundtrips per page-load × users,
 *     so monitor Supabase load after flipping.
 *
 *   RBAC_HOTRELOAD_DISABLED=1
 *     Disables BroadcastChannel + the 5min polling timer in the
 *     `useActiveTenant` hook. Tabs only refresh on hard-reload after
 *     this. Use when you suspect the polling is spamming requests
 *     (which the rate-limit should already block, but defense-in-depth).
 *
 * Default for both: not disabled (the new behavior is active). Setting
 * the env var to anything truthy (`1`, `true`, `yes`) disables.
 *
 * Why env vars and not the calendar FeatureFlags system: that one is
 * scoped to calendar concerns and has tenant-percentage-rollout logic
 * we don't need here. A boolean ops-flag is the right tool for a
 * production kill-switch.
 */

const truthy = (v: string | undefined) =>
  v === '1' || v === 'true' || v === 'yes';

/**
 * Server-side: read once at module load. Vercel cold-start gives us
 * the latest value without an explicit reload step.
 */
export const RBAC_CACHE_DISABLED = truthy(process.env.RBAC_CACHE_DISABLED);

/**
 * Client-side: read from `NEXT_PUBLIC_*` so the bundle picks it up.
 * Defaults to enabled. Flip via Vercel env + redeploy (no code change).
 */
export function isHotReloadDisabled(): boolean {
  // Reading inside a function so SSR + client both evaluate at runtime.
  if (typeof process !== 'undefined') {
    return truthy(process.env.NEXT_PUBLIC_RBAC_HOTRELOAD_DISABLED);
  }
  return false;
}
